import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { EntryPointService } from "@gen/tracker/v1/entry_point_connect";
import type { CampaignEntry, EntryPoint, EntryPointInput } from "@gen/tracker/v1/entry_point_pb";
import type { PartialMessage } from "@bufbuild/protobuf";
import { trackingDomainKeys } from "@/lib/services/tracking-domain-service";

// An entry point («Вход») is one path of a domain that hands each visit to a
// campaign behind it — by a token in the link (`?<token>`, `?c=<token>`), by a
// rule, by the visitor's sticky cookie, else a fallback
// (docs/specs/use-cases/entry-point.md). Every write republishes the domain.

const client = createClient(EntryPointService);

export const entryPointKeys = {
  all: ["entry_points"] as const,
  list: (domainId: string) => [...entryPointKeys.all, "list", domainId] as const,
  campaign: (campaignId: string) => [...entryPointKeys.all, "campaign", campaignId] as const,
};

/** The workspace's entry points; of one domain when `domainId` is given. */
export function useListEntryPoints(domainId = "", enabled = true) {
  return useQuery({
    queryKey: entryPointKeys.list(domainId),
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<EntryPoint[]> => (await client.listEntryPoints({ trackingDomainId: domainId })).items,
  });
}

/** The entry points a campaign sits behind, each with its token and ad link. */
export function useCampaignEntries(campaignId: string | undefined) {
  return useQuery({
    queryKey: entryPointKeys.campaign(campaignId ?? ""),
    enabled: !!campaignId,
    queryFn: async (): Promise<CampaignEntry[]> => (await client.listCampaignEntries({ campaignId: campaignId! })).items,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: entryPointKeys.all });
    void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
  };
}

export function useSaveEntryPoint() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (v: { id?: string; trackingDomainId: string; input: PartialMessage<EntryPointInput> }): Promise<EntryPoint> =>
      v.id
        ? (await client.updateEntryPoint({ id: v.id, input: v.input })).entryPoint!
        : (await client.createEntryPoint({ trackingDomainId: v.trackingDomainId, input: v.input })).entryPoint!,
    onSuccess: invalidate,
  });
}

export function useArchiveEntryPoint() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => void (await client.archiveEntryPoint({ id })),
    onSuccess: invalidate,
  });
}

/** Puts a campaign behind an entry point; an empty token mints a new UUID, a second call changes it. */
export function useBindCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (v: { entryPointId: string; campaignId: string; token?: string }): Promise<CampaignEntry> =>
      (await client.bindCampaign({ entryPointId: v.entryPointId, campaignId: v.campaignId, token: v.token ?? "" })).entry!,
    onSuccess: invalidate,
  });
}

export function useUnbindCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (v: { entryPointId: string; campaignId: string }) => void (await client.unbindCampaign(v)),
    onSuccess: invalidate,
  });
}
